import { useState, useEffect } from "react";

const API = "https://scraps-backend.onrender.com";
const FONT = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Inter:wght@400;500;600;700;800&display=swap');`;

function MembersTab({ leagueId, token, league }) {
  const isMobile = window.innerWidth < 768;
  const [teams, setTeams] = useState([]);
  const [adjustTeam, setAdjustTeam] = useState(null);
  const [newCredits, setNewCredits] = useState("");
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [sendingInvites, setSendingInvites] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const [refreshDelta, setRefreshDelta] = useState("");
  const [refreshNotify, setRefreshNotify] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const sendInvites = async () => {
    const emails = inviteEmails.split(/[\n,]+/).map(e=>e.trim()).filter(e=>e.includes("@"));
    if (emails.length === 0) { alert("Please enter at least one valid email address."); return; }
    setSendingInvites(true);
    const res = await fetch(`${API}/league/${leagueId}/send-invites`, {
      method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body:JSON.stringify({ emails, invited_by: league?.admin_name || "Your league admin" }),
    });
    const data = await res.json();
    setSendingInvites(false);
    setShowInvite(false);
    setInviteEmails("");
    alert(`✅ Sent invites to ${data.sent} people!`);
  };

  useEffect(() => { loadTeams(); }, []);

  const loadTeams = async () => {
    const res = await fetch(`${API}/league/${leagueId}/teams`);
    setTeams(await res.json());
  };

  const adjustCredits = async () => {
    if (!newCredits || isNaN(newCredits)) return;
    setSaving(true);
    await fetch(`${API}/league/${leagueId}/admin/adjust-credits`, {
      method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body:JSON.stringify({ team_id:adjustTeam.id, new_credits:Number(newCredits) }),
    });
    setAdjustTeam(null); setNewCredits(""); setSaving(false); loadTeams();
  };

  const refreshCredits = async () => {
    const delta = Number(refreshDelta);
    if (!refreshDelta || isNaN(delta) || delta === 0) { alert("Enter a non-zero amount."); return; }
    const verb = delta > 0 ? "Add" : "Remove";
    if (!window.confirm(`${verb} ${Math.abs(delta).toLocaleString()} credits for all ${teams.length} team(s) in this league? This won't change anyone's season stats.${refreshNotify ? " Members will get an email about it." : ""}`)) return;
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/league/${leagueId}/admin/refresh-credits`, {
        method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
        body:JSON.stringify({ delta, notify: refreshNotify }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to refresh credits");
      alert(`✅ ${data.message}`);
      setShowRefresh(false); setRefreshDelta("");
    } catch(e) { alert("Error: " + e.message); }
    setRefreshing(false); loadTeams();
  };

  const removePlayer = async (team) => {
    if (!window.confirm(`Remove ${team.team_name}? Their pending bets will be cancelled and credits refunded.`)) return;
    await fetch(`${API}/league/${leagueId}/admin/remove-player/${team.id}`, {
      method:"DELETE", headers:{ Authorization:`Bearer ${token}` },
    });
    loadTeams();
  };

  // Sort teams alphabetically by owner name
  const sorted = [...teams].sort((a,b) => (a.owner_name||"").localeCompare(b.owner_name||""));

  return (
    <div>
      {showRefresh && (
        <div onClick={()=>setShowRefresh(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:"#fff", borderRadius:16, padding:28, width:360, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#111827", marginBottom:4, fontFamily:"'Inter',sans-serif" }}>Refresh League Credits</div>
            <div style={{ fontSize:13, color:"#6b7280", marginBottom:20, fontFamily:"'Inter',sans-serif" }}>Applies to all {teams.length} team(s). Doesn't affect season stats.</div>
            <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:5, fontFamily:"'Inter',sans-serif" }}>CREDITS TO ADD (use a negative number to remove)</label>
            <input type="number" value={refreshDelta} onChange={e=>setRefreshDelta(e.target.value)} placeholder="e.g. 15000" style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, marginBottom:14, outline:"none", boxSizing:"border-box", fontFamily:"'Inter',sans-serif" }}/>
            <label style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              <input type="checkbox" checked={refreshNotify} onChange={e=>setRefreshNotify(e.target.checked)} style={{ width:16, height:16 }}/>
              <span style={{ fontSize:13, color:"#374151" }}>Email members to let them know</span>
            </label>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={refreshCredits} disabled={refreshing} style={{ flex:1, padding:10, background:"#3d9e7a", color:"#fff", border:"none", borderRadius:8, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>{refreshing?"Applying...":"Apply to All Teams"}</button>
              <button onClick={()=>setShowRefresh(false)} style={{ flex:1, padding:10, background:"transparent", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {adjustTeam && (
        <div onClick={()=>setAdjustTeam(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:"#fff", borderRadius:16, padding:28, width:340, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#111827", marginBottom:4, fontFamily:"'Inter',sans-serif" }}>Adjust Credits</div>
            <div style={{ fontSize:13, color:"#6b7280", marginBottom:20, fontFamily:"'Inter',sans-serif" }}>{adjustTeam.team_name} · Current: {adjustTeam.credits?.toLocaleString()}</div>
            <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:5, fontFamily:"'Inter',sans-serif" }}>NEW AMOUNT</label>
            <input type="number" value={newCredits} onChange={e=>setNewCredits(e.target.value)} placeholder={adjustTeam.credits} style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, marginBottom:16, outline:"none", boxSizing:"border-box", fontFamily:"'Inter',sans-serif" }}/>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={adjustCredits} disabled={saving} style={{ flex:1, padding:10, background:"#3d9e7a", color:"#fff", border:"none", borderRadius:8, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>{saving?"Saving...":"Save"}</button>
              <button onClick={()=>setAdjustTeam(null)} style={{ flex:1, padding:10, background:"transparent", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background:"#f0fdf8", border:"1px solid #a7f3d0", borderRadius:10, padding:"12px 16px", marginBottom:20, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        <span style={{ fontSize:13, color:"#065f46", fontFamily:"'Inter',sans-serif" }}>Invite code:</span>
        <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:"#065f46", letterSpacing:"0.1em" }}>{league?.invite_code}</span>
        <span style={{ fontSize:12, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>· Share with members to join</span>
        <button onClick={()=>setShowRefresh(true)} style={{ marginLeft:"auto", padding:"6px 14px", background:"#3d9e7a", border:"none", borderRadius:7, color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
          💰 Refresh Credits
        </button>
        <button onClick={()=>setShowInvite(!showInvite)} style={{ padding:"6px 14px", background:"#0d1b2a", border:"none", borderRadius:7, color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
          ✉️ Invite by Email
        </button>
      </div>

      {showInvite && (
        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px", marginBottom:20, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:14, fontWeight:600, color:"#111827", marginBottom:6, fontFamily:"'Inter',sans-serif" }}>Invite members by email</div>
          <div style={{ fontSize:12, color:"#9ca3af", marginBottom:12, fontFamily:"'Inter',sans-serif" }}>Enter email addresses separated by commas or new lines. Each person will get an email with the invite code.</div>
          <textarea value={inviteEmails} onChange={e=>setInviteEmails(e.target.value)} placeholder="friend@email.com, another@email.com" rows={3} style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box", resize:"vertical", marginBottom:10 }}/>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={sendInvites} disabled={sendingInvites} style={{ padding:"8px 20px", background:"#3d9e7a", border:"none", borderRadius:7, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              {sendingInvites ? "Sending..." : "Send Invites"}
            </button>
            <button onClick={()=>{ setShowInvite(false); setInviteEmails(""); }} style={{ padding:"8px 16px", background:"transparent", border:"1px solid #e5e7eb", borderRadius:7, color:"#6b7280", fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background:"#fff", border:"1px solid #f3f4f6", borderRadius:12, overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", tableLayout:"fixed", minWidth:isMobile?500:"auto" }}>
          <colgroup>
            <col style={{ width:"32px" }}/>
            <col style={{ width:"30%" }}/>
            <col style={{ width:"30%" }}/>
            <col style={{ width:"90px" }}/>
            <col style={{ width:"70px" }}/>
            <col style={{ width:"130px" }}/>
          </colgroup>
          <thead>
            <tr style={{ borderBottom:"1px solid #f3f4f6", background:"#f9fafb" }}>
              <th style={{ padding:"10px 8px", textAlign:"left", fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:"0.04em" }}></th>
              <th style={{ padding:"10px 8px", textAlign:"left", fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:"0.04em" }}>TEAM</th>
              <th style={{ padding:"10px 8px", textAlign:"left", fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:"0.04em" }}>OWNER</th>
              <th style={{ padding:"10px 8px", textAlign:"right", fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:"0.04em" }}>CREDITS</th>
              <th style={{ padding:"10px 8px", textAlign:"right", fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:"0.04em" }}>+/-</th>
              <th style={{ padding:"10px 8px", textAlign:"right", fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:"0.04em" }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((team, i) => {
              const change = team.credits - team.starting_credits;
              const rank = teams.slice().sort((a,b)=>b.credits-a.credits).findIndex(t=>t.id===team.id);
              const rankLabel = rank===0?"🥇":rank===1?"🥈":rank===2?"🥉":`#${rank+1}`;
              return (
                <tr key={team.id} style={{ borderBottom:"1px solid #f9fafb" }}>
                  <td style={{ padding:"12px 8px", fontSize:rank<3?16:13, textAlign:"center", color:"#9ca3af" }}>{rankLabel}</td>
                  <td style={{ padding:"12px 8px", overflow:"hidden" }}>
                    <div style={{ fontSize:14, fontWeight:600, color:"#111827", fontFamily:"'Inter',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {team.team_name}
                      {team.is_admin && <span style={{ marginLeft:6, fontSize:10, padding:"1px 6px", background:"#f0fdf8", color:"#3d9e7a", border:"1px solid #a7f3d0", borderRadius:4, fontWeight:700, verticalAlign:"middle" }}>ADMIN</span>}
                    </div>
                  </td>
                  <td style={{ padding:"12px 8px", overflow:"hidden" }}>
                    <div style={{ fontSize:13, color:"#374151", fontFamily:"'Inter',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{team.owner_name}</div>
                    {team.email && <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'Inter',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{team.email}</div>}
                  </td>
                  <td style={{ padding:"12px 8px", textAlign:"right", fontSize:14, fontWeight:700, color:"#111827", fontFamily:"'Inter',sans-serif" }}>{team.credits?.toLocaleString()}</td>
                  <td style={{ padding:"12px 8px", textAlign:"right", fontSize:13, fontWeight:600, color:change>0?"#3d9e7a":change<0?"#ef4444":"#9ca3af", fontFamily:"'Inter',sans-serif" }}>{change>0?"+":""}{change?.toLocaleString()}</td>
                  <td style={{ padding:"8px 8px", textAlign:"right" }}>
                    <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                      <button onClick={()=>{ setAdjustTeam(team); setNewCredits(team.credits); }} style={{ padding:"5px 10px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:6, color:"#374151", cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif" }}>Adjust</button>
                      {!team.is_admin && <button onClick={()=>removePlayer(team)} style={{ padding:"5px 10px", background:"#fff5f5", border:"1px solid #fecaca", borderRadius:6, color:"#dc2626", cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif" }}>Remove</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function BetsTab({ leagueId, token }) {
  const [bets, setBets] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [settling, setSettling] = useState(null);

  useEffect(() => { loadBets(); loadTeams(); }, []);

  const loadBets = async () => {
    const res = await fetch(`${API}/league/${leagueId}/bets`);
    setBets(await res.json());
  };

  const loadTeams = async () => {
    const res = await fetch(`${API}/league/${leagueId}/teams`);
    setTeams(await res.json());
  };

  const getTeamName = (teamId) => teams.find(t=>t.id===teamId)?.team_name || teamId;

  const settleBet = async (betId, result) => {
    setSettling(betId);
    await fetch(`${API}/league/${leagueId}/admin/settle-bet`, {
      method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body:JSON.stringify({ bet_id:betId, result }),
    });
    setSettling(null); loadBets();
  };

  const filtered = bets.filter(b => filter==="all" ? true : b.status===filter);

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
        {["pending","settled","all"].map(f => (
          <button key={f} onClick={()=>setFilter(f)} style={{ padding:"6px 16px", borderRadius:20, border:"1px solid", borderColor:filter===f?"#3d9e7a":"#e5e7eb", background:filter===f?"#f0fdf8":"#fff", color:filter===f?"#3d9e7a":"#6b7280", fontFamily:"'Inter',sans-serif", fontWeight:600, fontSize:12, cursor:"pointer" }}>
            {f.charAt(0).toUpperCase()+f.slice(1)} {f!=="all"&&`(${bets.filter(b=>b.status===f).length})`}
          </button>
        ))}
      </div>
      {filtered.length===0 && <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>No {filter} bets</div>}
      {filtered.map(bet => (
        <div key={bet.id} style={{ background:"#fff", border:"1px solid #f3f4f6", borderRadius:12, padding:"14px 16px", marginBottom:10, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:2, fontFamily:"'Inter',sans-serif" }}>
                {bet.selection}{bet.point ? ` ${bet.point>0?"+":""}${bet.point}` : ""}
              </div>
              <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'Inter',sans-serif", marginBottom:6 }}>
                {bet.is_parlay && bet.parlay_legs?.length > 0
                  ? bet.parlay_legs.map(l=>l.gameName||l.game_name).filter(Boolean).join(" · ")
                  : bet.game_name}
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                <span style={{ fontSize:11, padding:"2px 8px", background:"#eff6ff", color:"#3b82f6", borderRadius:4, fontFamily:"'Inter',sans-serif", fontWeight:600 }}>{bet.market_label}</span>
                <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>{getTeamName(bet.team_id)}</span>
                {bet.is_parlay && <span style={{ fontSize:11, padding:"2px 8px", background:"#fff7ed", color:"#ea580c", borderRadius:4, fontFamily:"'Inter',sans-serif", fontWeight:600 }}>Parlay</span>}
              </div>
            </div>
            <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
              <div style={{ fontSize:16, fontWeight:800, color:"#111827", fontFamily:"'Inter',sans-serif" }}>{bet.wager?.toLocaleString()}</div>
              <div style={{ fontSize:12, color:"#3d9e7a", fontFamily:"'Inter',sans-serif" }}>to win {bet.to_win?.toLocaleString()}</div>
              {bet.status==="settled" && (
                <div style={{ marginTop:4, fontSize:12, fontWeight:700, color:bet.result==="won"?"#3d9e7a":bet.result==="lost"?"#ef4444":"#f59e0b", fontFamily:"'Inter',sans-serif" }}>
                  {bet.result?.charAt(0).toUpperCase()+bet.result?.slice(1)}
                </div>
              )}
            </div>
          </div>
          {bet.status==="pending" && (
            <div style={{ display:"flex", gap:8, paddingTop:10, borderTop:"1px solid #f3f4f6", alignItems:"center" }}>
              <span style={{ fontSize:12, color:"#9ca3af", fontFamily:"'Inter',sans-serif" }}>Settle:</span>
              {["won","lost","push"].map(result => (
                <button key={result} onClick={()=>settleBet(bet.id,result)} disabled={settling===bet.id} style={{ padding:"5px 14px", border:"1px solid", borderColor:result==="won"?"#a7f3d0":result==="lost"?"#fecaca":"#fcd34d", background:result==="won"?"#f0fdf8":result==="lost"?"#fff5f5":"#fffbeb", color:result==="won"?"#3d9e7a":result==="lost"?"#dc2626":"#92400e", borderRadius:6, cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
                  {settling===bet.id?"...":result.charAt(0).toUpperCase()+result.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SettingsTab({ leagueId, token, league }) {
  const [maxBet, setMaxBet] = useState(league?.max_bet||"");
  const [seasonEnd, setSeasonEnd] = useState(league?.season_end||"");
  const [locked, setLocked] = useState(league?.locked||false);
  const [allowMultiple, setAllowMultiple] = useState(league?.allow_multiple_entries||false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    await fetch(`${API}/league/${leagueId}/settings`, {
      method:"PUT", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body:JSON.stringify({ max_bet:maxBet?Number(maxBet):null, season_end:seasonEnd||null, locked, allow_multiple_entries:allowMultiple }),
    });
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2000);
  };

  const Toggle = ({ value, onChange, label, desc }) => (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 0", borderBottom:"1px solid #f3f4f6" }}>
      <div>
        <div style={{ fontSize:14, fontWeight:600, color:"#111827", fontFamily:"'Inter',sans-serif" }}>{label}</div>
        <div style={{ fontSize:12, color:"#9ca3af", fontFamily:"'Inter',sans-serif", marginTop:2 }}>{desc}</div>
      </div>
      <div onClick={()=>onChange(!value)} style={{ width:44, height:24, borderRadius:12, background:value?"#3d9e7a":"#e5e7eb", cursor:"pointer", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
        <div style={{ position:"absolute", top:3, left:value?23:3, width:18, height:18, borderRadius:"50%", background:"#fff", transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }}/>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth:480 }}>
      <div style={{ background:"#fff", border:"1px solid #f3f4f6", borderRadius:14, padding:"20px", marginBottom:16, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:16, fontFamily:"'Inter',sans-serif" }}>League Settings</div>
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:5, fontFamily:"'Inter',sans-serif", letterSpacing:"0.04em" }}>MAX BET PER WAGER</label>
        <input type="number" value={maxBet} onChange={e=>setMaxBet(e.target.value)} placeholder="No limit" style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, marginBottom:16, outline:"none", boxSizing:"border-box", fontFamily:"'Inter',sans-serif" }}/>
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:5, fontFamily:"'Inter',sans-serif", letterSpacing:"0.04em" }}>SEASON END DATE</label>
        <input type="date" value={seasonEnd} onChange={e=>setSeasonEnd(e.target.value)} style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, marginBottom:16, outline:"none", boxSizing:"border-box", fontFamily:"'Inter',sans-serif" }}/>
        <Toggle value={allowMultiple} onChange={setAllowMultiple} label="Allow multiple entries" desc="Members can join with more than one team" />
        <Toggle value={locked} onChange={setLocked} label="Lock league" desc="Prevent new members from joining" />
        <button onClick={save} disabled={saving} style={{ width:"100%", padding:12, background:saved?"#f0fdf8":"#3d9e7a", color:saved?"#3d9e7a":"#fff", border:saved?"1px solid #a7f3d0":"none", borderRadius:8, fontWeight:600, cursor:"pointer", fontSize:14, fontFamily:"'Inter',sans-serif", marginTop:16 }}>
          {saved?"✓ Saved":saving?"Saving...":"Save Settings"}
        </button>
      </div>
      <div style={{ background:"#f9fafb", border:"1px solid #f3f4f6", borderRadius:14, padding:"20px" }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:12, fontFamily:"'Inter',sans-serif" }}>League Info</div>
        {[
          ["League ID", leagueId, "monospace"],
          ["Invite code", league?.invite_code, "'Barlow Condensed',sans-serif"],
          ["Starting credits", league?.starting_credits?.toLocaleString(), "'Inter',sans-serif"],
          ["Status", league?.status, "'Inter',sans-serif"],
        ].map(([label, value, font]) => (
          <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingBottom:8, marginBottom:8, borderBottom:"1px solid #f3f4f6" }}>
            <span style={{ fontSize:13, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>{label}</span>
            <span style={{ fontSize:13, fontWeight:600, color:"#111827", fontFamily:font }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function H2HStartTab({ leagueId, token, league }) {
  const [startDate, setStartDate] = useState("");
  const [starting, setStarting] = useState(false);
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editSettings, setEditSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  const ALL_SPORTS = [
    { key:"americanfootball_nfl", label:"🏈 NFL" },
    { key:"basketball_nba", label:"🏀 NBA" },
    { key:"baseball_mlb", label:"⚾ MLB" },
    { key:"icehockey_nhl", label:"🏒 NHL" },
    { key:"americanfootball_ncaaf", label:"🏈 NCAAF" },
    { key:"basketball_ncaab", label:"🏀 NCAAB" },
    { key:"soccer_epl", label:"⚽ EPL" },
  ];

  useEffect(() => {
    fetch(`${API}/league/${leagueId}/teams`).then(r=>r.json()).then(setMembers);
    fetch(`${API}/h2h/${leagueId}/settings`).then(r=>r.json()).then(s => { setSettings(s); setEditSettings(s); });
  }, [leagueId]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/h2h/${leagueId}/admin/update-settings`, {
        method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
        body: JSON.stringify(editSettings),
      });
      if (res.ok) { alert("Settings saved!"); setSettings(editSettings); }
      else alert("Failed to save settings");
    } catch(e) { alert("Error: " + e.message); }
    setSavingSettings(false);
  };

  const getPreviewWeeks = () => {
    if (!startDate || !editSettings.period_length_days) return [];
    const weeks = [];
    const [year, month, day] = startDate.split("-").map(Number);
    let start = new Date(year, month - 1, day);
    const days = editSettings.period_length_days || 7;
    const total = (editSettings.total_regular_periods || 1) +
      (editSettings.playoff_teams > 0 ? Math.ceil(Math.log2(Math.max(editSettings.playoff_teams, 2))) * (editSettings.playoff_rounds_per_period || 1) : 0);
    for (let i = 0; i < Math.min(total, 20); i++) {
      const end = new Date(start);
      end.setDate(end.getDate() + days - 1);
      weeks.push({ num: i+1, start: new Date(start), end: new Date(end), isPlayoff: i >= editSettings.total_regular_periods });
      start = new Date(start);
      start.setDate(start.getDate() + days);
    }
    return weeks;
  };

  const fmtDate = (d) => d.toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" });

  const startLeague = async () => {
    if (!startDate) { alert("Please select a start date"); return; }
    if (!window.confirm(`Start the league with ${members.length} members on ${startDate}?`)) return;
    setStarting(true);
    try {
      const res = await fetch(`${API}/h2h/${leagueId}/admin/start-league`, {
        method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
        body:JSON.stringify({ start_date: startDate }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      alert(`✅ League started! ${data.regular_periods} matchups scheduled`);
    } catch(e) { alert("Error: " + e.message); }
    setStarting(false);
  };

  if (league?.status === "active") return (
    <div style={{ maxWidth:560 }}>
      <div style={{ background:"#f0fdf8", border:"1.5px solid #a7f3d0", borderRadius:12, padding:20, textAlign:"center", marginBottom:16 }}>
        <div style={{ fontSize:28, marginBottom:8 }}>✅</div>
        <div style={{ fontSize:18, fontWeight:700, color:"#065f46", marginBottom:4, fontFamily:"'Inter',sans-serif" }}>League is Active</div>
        <div style={{ fontSize:13, color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>Matchups auto-advance at 4am ET. No manual action needed!</div>
      </div>
    </div>
  );

  const previewWeeks = getPreviewWeeks();
  const n = members.length;
  const pt = editSettings.playoff_teams || 0;
  const canStart = n >= 2 && !!startDate && pt <= n;

  return (
    <div style={{ maxWidth:560 }}>
      <div style={{ background:"#fff", border:"1px solid #f3f4f6", borderRadius:14, padding:20, marginBottom:16, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:16, fontFamily:"'Inter',sans-serif" }}>⚙️ League Settings</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          {[
            { label:"Matchup Length", el:<select value={editSettings.period_length_days||7} onChange={e=>setEditSettings(s=>({...s,period_length_days:Number(e.target.value)}))} style={{ width:"100%", padding:"8px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif" }}><option value={1}>1 day</option><option value={3}>3 days</option><option value={5}>5 days</option><option value={7}>7 days</option><option value={14}>14 days</option></select> },
            { label:"Starting Credits", el:<input type="number" value={editSettings.starting_credits||1000} onChange={e=>setEditSettings(s=>({...s,starting_credits:Number(e.target.value)}))} style={{ width:"100%", padding:"8px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif" }}/> },
            { label:"Regular Matchups", el:<input type="number" value={editSettings.total_regular_periods||10} min={1} max={30} onChange={e=>setEditSettings(s=>({...s,total_regular_periods:Number(e.target.value)}))} style={{ width:"100%", padding:"8px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif" }}/> },
            { label:"Playoff Teams (0=none)", el:<input type="number" value={editSettings.playoff_teams||0} min={0} max={16} onChange={e=>setEditSettings(s=>({...s,playoff_teams:Number(e.target.value)}))} style={{ width:"100%", padding:"8px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif" }}/> },
          ].map(({label,el}) => (
            <div key={label}>
              <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4, fontFamily:"'Inter',sans-serif" }}>{label.toUpperCase()}</label>
              {el}
            </div>
          ))}
        </div>
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:8, fontFamily:"'Inter',sans-serif" }}>SPORTS</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {ALL_SPORTS.map(s => {
              const sel = (editSettings.sports||[]).includes(s.key);
              return <button key={s.key} onClick={()=>setEditSettings(es=>({...es,sports:sel?(es.sports||[]).filter(x=>x!==s.key):[...(es.sports||[]),s.key]}))} style={{ padding:"4px 12px", borderRadius:20, border:`1.5px solid ${sel?"#3d9e7a":"#e5e7eb"}`, background:sel?"#f0fdf8":"#fff", color:sel?"#3d9e7a":"#6b7280", fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>{s.label}</button>;
            })}
          </div>
        </div>
        <button onClick={saveSettings} disabled={savingSettings} style={{ width:"100%", padding:10, background:"#3d9e7a", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
          {savingSettings ? "Saving..." : "Save Settings"}
        </button>
      </div>

      <div style={{ background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:12, padding:16, marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:600, color:"#92400e", marginBottom:6, fontFamily:"'Inter',sans-serif" }}>⚠️ Before you start</div>
        {["Make sure all members have joined — roster locks permanently","Matchups auto-advance at 4am ET — no manual action needed"].map((w,i)=>(
          <div key={i} style={{ fontSize:12, color:"#92400e", display:"flex", gap:8, marginBottom:3, fontFamily:"'Inter',sans-serif" }}><span>·</span><span>{w}</span></div>
        ))}
      </div>

      <div style={{ background:"#fff", border:"1px solid #f3f4f6", borderRadius:14, padding:20, marginBottom:16, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:4, fontFamily:"'Inter',sans-serif" }}>📅 Start Date</div>
        <div style={{ fontSize:12, color:"#9ca3af", marginBottom:12, fontFamily:"'Inter',sans-serif" }}>Week 1 betting opens on this date</div>
        <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} min={new Date().toISOString().split("T")[0]} style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, outline:"none", boxSizing:"border-box", marginBottom:12, fontFamily:"'Inter',sans-serif" }}/>
        {previewWeeks.length > 0 && (
          <div style={{ maxHeight:180, overflowY:"auto" }}>
            <div style={{ fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:8, fontFamily:"'Inter',sans-serif" }}>SCHEDULE PREVIEW</div>
            {previewWeeks.map(w => (
              <div key={w.num} style={{ display:"flex", gap:12, padding:"5px 10px", background:w.isPlayoff?"#fffbeb":"#f9fafb", borderRadius:6, fontSize:12, marginBottom:3, fontFamily:"'Inter',sans-serif" }}>
                <span style={{ fontWeight:700, color:w.isPlayoff?"#92400e":"#3d9e7a", minWidth:80 }}>{w.isPlayoff?`Playoff ${w.num-editSettings.total_regular_periods}`:`Week ${w.num}`}</span>
                <span style={{ color:"#6b7280" }}>{fmtDate(w.start) === fmtDate(w.end) ? fmtDate(w.start) : `${fmtDate(w.start)} — ${fmtDate(w.end)}`}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={startLeague} disabled={starting||!canStart} style={{ width:"100%", padding:14, background:canStart?"#3d9e7a":"#e5e7eb", color:canStart?"#fff":"#9ca3af", border:"none", borderRadius:8, fontSize:15, fontWeight:600, cursor:canStart?"pointer":"not-allowed", fontFamily:"'Inter',sans-serif" }}>
        {starting?"Starting...":n<2?"Need at least 2 members":!startDate?"Select a start date":pt>n?"Fix playoff settings":`🚀 Start League with ${n} Members`}
      </button>
    </div>
  );
}

export default function LeagueAdmin({ onBack }) {
  const savedToken = localStorage.getItem("scraps_la_token");
  const savedLeague = localStorage.getItem("scraps_la_league");
  const [view, setView] = useState(savedToken && savedLeague ? "dashboard" : "login");
  const [token, setToken] = useState(savedToken || null);
  const [league, setLeague] = useState(null);
  const [leagueId, setLeagueId] = useState(savedLeague || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("members");
  const [leaderboard, setLeaderboard] = useState([]);
  const isMobile = window.innerWidth < 768;

  const login = async () => {
    if (!leagueId||!password) { setError("Please fill in all fields"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/league-admin/login`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ league_id:leagueId.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      localStorage.setItem("scraps_la_token", data.token);
      localStorage.setItem("scraps_la_league", leagueId.trim());
      setToken(data.token); setLeague(data.league);
      setView("dashboard");
      loadLeaderboard(data.league.id, data.token);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  useEffect(() => {
    const savedToken = localStorage.getItem("scraps_la_token");
    const savedLeague = localStorage.getItem("scraps_la_league");
    if (savedToken && savedLeague) {
      setToken(savedToken);
      setLeagueId(savedLeague);
      Promise.all([
        fetch(`${API}/league/${savedLeague}`).then(r=>r.json()),
        fetch(`${API}/league/${savedLeague}/leaderboard`).then(r=>r.json()),
      ]).then(([l, lb]) => {
        setLeague(l);
        setLeaderboard(lb || []);
        setView("dashboard");
      }).catch(()=>{});
    }
  }, []);

  const loadLeaderboard = async (lid) => {
    const res = await fetch(`${API}/league/${lid||leagueId}/leaderboard`);
    setLeaderboard(await res.json());
  };

  const logout = () => {
    localStorage.removeItem("scraps_la_token");
    localStorage.removeItem("scraps_la_league");
    setToken(null); setLeague(null);
    if (onBack) onBack();
  };

  if (view === "dashboard" && !league) return (
    <div style={{ height:"100vh", backgroundColor:"#f8f9fa", display:"flex", alignItems:"center", justifyContent:"center", color:"#6b7280", fontFamily:"'Inter',sans-serif", flexDirection:"column", gap:12 }}>
      <div style={{ fontSize:24 }}>⟳</div>
      <div>Loading league data...</div>
    </div>
  );

  if (view === "login") return (
    <div style={{ minHeight:"100vh", background:"#0d1b2a", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',sans-serif", padding:"32px 24px" }}>
      <style>{FONT}</style>
      <div style={{ background:"#ffffff", borderRadius:20, padding:"36px 32px", width:"100%", maxWidth:420 }}>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <img src="/scraps_logo.png" alt="Scraps" style={{ height:90, objectFit:"contain", marginBottom:12 }}/>
          <div style={{ fontSize:13, color:"#6b7280" }}>League Admin</div>
        </div>
        {error && <div style={{ background:"#fff5f5", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#dc2626", marginBottom:16 }}>{error}</div>}
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:5, letterSpacing:"0.04em" }}>LEAGUE ID</label>
        <input value={leagueId} onChange={e=>setLeagueId(e.target.value)} placeholder="your-league-id" style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, marginBottom:12, outline:"none", boxSizing:"border-box" }}/>
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:5, letterSpacing:"0.04em" }}>ADMIN PASSWORD</label>
        <input type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} placeholder="Your admin password" style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, marginBottom:16, outline:"none", boxSizing:"border-box" }}/>
        <button onClick={login} disabled={loading} style={{ width:"100%", padding:12, background:"#3d9e7a", color:"#fff", border:"none", borderRadius:8, fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:10 }}>
          {loading?"Signing in...":"Sign In"}
        </button>
        <button onClick={onBack} style={{ width:"100%", padding:10, background:"transparent", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, cursor:"pointer", fontSize:13 }}>← Back</button>
      </div>
    </div>
  );

  const tabs = ["members","bets","leaderboard","settings",...(league?.league_type==="h2h"?["start"]:[])];
  const tabLabels = { members:"Members", bets:"Bets", leaderboard:"Leaderboard", settings:"Settings", start:"🚀 Start" };

  return (
    <div style={{ minHeight:"100vh", background:"#1e2a3a", fontFamily:"'Inter',sans-serif" }}>
      <style>{FONT}</style>

      {/* Header */}
      <div style={{ background:"#0d1b2a", padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, minWidth:0 }}>
          <button onClick={onBack} style={{ background:"none", border:"none", color:"#fff", cursor:"pointer", fontSize:24, padding:0, lineHeight:1, fontWeight:900, flexShrink:0 }}>≡</button>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{league?.name}</div>
            <div style={{ fontSize:11, color:"#3d9e7a", fontWeight:600 }}>Admin Panel</div>
          </div>
        </div>
        <button onClick={logout} style={{ padding:"6px 14px", background:"transparent", border:"1px solid rgba(255,255,255,0.2)", borderRadius:6, color:"#f0f4ff", cursor:"pointer", fontSize:12, flexShrink:0 }}>Logout</button>
      </div>

      {/* Stats bar */}
      <div style={{ background:"#fff", borderBottom:"1px solid #f3f4f6", padding:isMobile?"10px 16px":"12px 20px", display:"flex", gap:0, overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
        {[
          { label:"Members", value:leaderboard.length },
          { label:"Invite Code", value:league?.invite_code, mono:true, color:"#3d9e7a" },
          { label:"Starting Credits", value:league?.starting_credits?.toLocaleString() },
          { label:"Max Bet", value:league?.max_bet?league.max_bet.toLocaleString()+" cr":"No limit" },
        ].map((s,i) => (
          <div key={s.label} style={{ flex:"0 0 auto", paddingRight:isMobile?16:24, marginRight:isMobile?16:24, borderRight:i<3?"1px solid #f3f4f6":"none" }}>
            <div style={{ fontSize:9, fontWeight:600, color:"#9ca3af", letterSpacing:"0.04em", marginBottom:2 }}>{s.label.toUpperCase()}</div>
            <div style={{ fontSize:isMobile?13:16, fontWeight:700, color:s.color||"#111827", fontFamily:s.mono?"'Barlow Condensed',sans-serif":"'Inter',sans-serif", letterSpacing:s.mono?"0.08em":0 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ background:"#fff", borderBottom:"1px solid #f3f4f6", padding:"0 16px", display:"flex", justifyContent:isMobile?"flex-start":"center", overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
        {tabs.map(tab => (
          <button key={tab} onClick={()=>{ setActiveTab(tab); if(tab==="leaderboard") loadLeaderboard(); }} style={{ padding:"12px 16px", background:"transparent", border:"none", borderBottom:activeTab===tab?"2px solid #3d9e7a":"2px solid transparent", color:activeTab===tab?"#3d9e7a":"#6b7280", fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:activeTab===tab?700:500, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:isMobile?"12px":"20px", maxWidth:860, margin:"0 auto" }}>
        {activeTab==="members" && <MembersTab leagueId={league?.id} token={token} league={league}/>}
        {activeTab==="bets" && <BetsTab leagueId={league?.id} token={token}/>}
        {activeTab==="settings" && <SettingsTab leagueId={league?.id} token={token} league={league}/>}
        {activeTab==="start" && league?.league_type==="h2h" && <H2HStartTab leagueId={league?.id} token={token} league={league}/>}
        {activeTab==="leaderboard" && (
          <div>
            {leaderboard.map((team,i) => {
              const change = team.credit_change;
              return (
                <div key={team.team_id} style={{ background:"#fff", border:"1px solid #f3f4f6", borderRadius:12, padding:"13px 16px", marginBottom:8, display:"flex", alignItems:"center", gap:14, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
                  <span style={{ fontSize:i<3?20:13, minWidth:28, textAlign:"center", color:"#9ca3af", fontWeight:600 }}>
                    {i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}
                  </span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{team.team_name}</div>
                    <div style={{ fontSize:11, color:"#9ca3af" }}>{team.owner_name}{team.pending_bets>0?` · ${team.pending_bets} pending`:""}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:16, fontWeight:800, color:"#111827" }}>{team.credits?.toLocaleString()}</div>
                    <div style={{ fontSize:12, fontWeight:600, color:change>0?"#3d9e7a":change<0?"#ef4444":"#9ca3af" }}>{change>0?"+":""}{change?.toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
